import { AlertTriangle } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/button";

interface RemoveMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantName: string;
  email: string;
  removeMember: (email: string) => Promise<boolean>;
  isSubmitting: boolean;
}

export function RemoveMemberDialog({
  open,
  onOpenChange,
  tenantName,
  email,
  removeMember,
  isSubmitting,
}: RemoveMemberDialogProps) {
  const { t } = useTranslation(["admin"]);

  const handleConfirm = async () => {
    const success = await removeMember(email);
    if (success) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        onInteractOutside={(e) => {
          if (isSubmitting) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isSubmitting) e.preventDefault();
        }}
      >
        <DialogHeader className="space-y-3">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <DialogTitle>
            {t("admin:tenants.detail.removeMemberDialog.title")}
          </DialogTitle>
          <DialogDescription className="text-sm">
            <Trans
              t={t}
              i18nKey="admin:tenants.detail.removeMemberDialog.description"
              values={{ email, tenantName }}
            />
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <DialogClose asChild>
            <Button
              variant="outline"
              disabled={isSubmitting}
              testId="remove-member-cancel"
            >
              {t("admin:tenants.detail.removeMemberDialog.cancel")}
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            loading={isSubmitting}
            disabled={isSubmitting}
            testId="remove-member-confirm"
          >
            {isSubmitting
              ? t("admin:tenants.detail.removeMemberDialog.removing")
              : t("admin:tenants.detail.removeMemberDialog.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
